import classNames from "classnames";
import React from "react";
import { t } from "ttag";

import { ProductOptionSelectorSet } from "../../../components/ProductOptionSelectorSet";
import { ConfiguratorTypes } from "../../../constants";
import {
    IOptionCode,
    IOptionSingleValue,
    IProduct,
} from "../../../models/catalogue";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { getProductVariant } from "../../../store/selectors";
import { defaults } from "../defaults";
import { setOptionValue, setQuantity } from "../reducers";

import styles from "./index.module.scss";

interface IQuantitySelectorProps {
    rootProduct: IProduct;
    quantity: number;
    onQuantityChange: (e: React.FormEvent<HTMLSelectElement>) => void;
}

export const QuantitySelector = (
    props: IQuantitySelectorProps,
): React.JSX.Element => {
    const wrapperClasses = classNames({
        [styles.selectContainer]: true,
        "grid-card-configurator__select-container--product-grid": true,
    });
    const inputID = `quantity-${props.rootProduct.id}`;
    const options = Array<number>(5)
        .fill(0)
        .map((_, i) => i + 1)
        .map((i) => (
            <option key={i} value={i}>
                {i}
            </option>
        ));
    return (
        <div className={wrapperClasses}>
            <label htmlFor={inputID}>{t`Qty:`}</label>
            <select
                id={inputID}
                value={props.quantity}
                onChange={props.onQuantityChange}
            >
                <optgroup label={t`Qty:`}>{options}</optgroup>
            </select>
        </div>
    );
};

const sortOptionCodes = (optionCodes: IOptionCode[]): IOptionCode[] => {
    const sorted = [...optionCodes];
    // If feel option exists, move it to the end (to sort correctly for the UI)
    const hasFeel = sorted.indexOf("option_feel");
    if (hasFeel !== -1) {
        sorted.push(sorted.splice(hasFeel, 1)[0]);
    }
    return sorted;
};

export const ProductCardConfigurator = React.memo(
    (props: { rootProduct: IProduct }): React.ReactNode => {
        const dispatch = useAppDispatch();

        const state = useAppSelector(
            (rootState) => rootState.productgrid2 || defaults,
        );
        const variant = useAppSelector((_rootState) =>
            getProductVariant([props.rootProduct], state.optionValues),
        );

        const handleOptionChange = (
            namespace: string,
            code: IOptionCode,
            index: number,
            totalNumValues: number,
            value: IOptionSingleValue,
        ): void => {
            dispatch(
                setOptionValue({
                    namespace: namespace,
                    code: code,
                    index: index,
                    totalNumValues: totalNumValues,
                    value: value,
                }),
            );
        };

        const handleQuantityChange = (
            event: React.FormEvent<HTMLSelectElement>,
        ): void => {
            const qty = parseInt(event.currentTarget.value, 10);
            dispatch(setQuantity(qty));
        };

        const wrapperClass = classNames({
            [styles.options]: true,
        });

        const optionCodes = sortOptionCodes([
            ...(props.rootProduct.attributes.product_options?.value || []),
        ]);

        return (
            <div className={wrapperClass}>
                {optionCodes.map((code) => (
                    <ProductOptionSelectorSet
                        key={code}
                        selectorID={code}
                        rootProduct={props.rootProduct}
                        variant={variant}
                        code={code}
                        values={state.optionValues}
                        onOptionChange={handleOptionChange}
                        configuratorType={ConfiguratorTypes.UPDATED}
                        style={"full"}
                    />
                ))}
                <QuantitySelector
                    rootProduct={props.rootProduct}
                    quantity={state.quantity}
                    onQuantityChange={handleQuantityChange}
                />
            </div>
        );
    },
);
